import { Icons } from "../components/Icons";
import type { RuleMatchField } from "../api/types";
import type { TawaRole } from "./auth/useRole";

export const HOME_PATH = "/hier";
export const MORE_PATH = "/plus";
export const RULES_PATH = "/regles-imputation";

export interface NavItem {
  id: string;
  label: string;
  icon: (typeof Icons)[keyof typeof Icons];
  path: string;
}

const HIER: NavItem = {
  id: "hier",
  label: "Hier",
  icon: Icons.home,
  path: HOME_PATH,
};
const FEUILLE: NavItem = {
  id: "timesheet",
  label: "Feuille",
  icon: Icons.cal,
  path: "/timesheet",
};
const A_VALIDER: NavItem = {
  id: "revue",
  label: "À valider",
  icon: Icons.check,
  path: "/revue",
};
const FACTURATION: NavItem = {
  id: "facturation",
  label: "Facturation",
  icon: Icons.invoice,
  path: "/facturation",
};
const CHANTIERS: NavItem = {
  id: "chantiers",
  label: "Chantiers",
  icon: Icons.tractor,
  path: "/chantiers",
};

const TERRAIN: NavItem = {
  id: "terrain",
  label: "Terrain",
  icon: Icons.tools,
  path: "/terrain",
};
const TACHES: NavItem = {
  id: "taches",
  label: "Mes tâches",
  icon: Icons.list,
  path: "/taches",
};
const PROJETS: NavItem = {
  id: "projets",
  label: "Projets",
  icon: Icons.briefcase,
  path: "/projets",
};
const EQUIPE: NavItem = {
  id: "equipe",
  label: "Mon équipe",
  icon: Icons.team,
  path: "/equipe?tab=temps",
};
const REGLES: NavItem = {
  id: "regles",
  label: "Règles d'imputation",
  icon: Icons.edit,
  path: RULES_PATH,
};
const ANOMALIES: NavItem = {
  id: "anomalies",
  label: "Anomalies",
  icon: Icons.bell,
  path: "/anomalies",
};
const ADMIN: NavItem = {
  id: "admin",
  label: "Administration",
  icon: Icons.shield,
  path: "/admin",
};
const BADGEUSE: NavItem = {
  id: "badger",
  label: "Badgeuse",
  icon: Icons.badge,
  path: "/badger",
};
const PROFIL: NavItem = {
  id: "profil",
  label: "Profil",
  icon: Icons.user,
  path: "/profil",
};
const RECAP: NavItem = {
  id: "recap",
  label: "Récap de la veille",
  icon: Icons.sync,
  path: "/recap",
};

export interface RoleNav {
  /** Entrées toujours visibles. Le temps d'abord, puis Facturation pour qui
   * dirige : avec « Plus », cinq onglets au plus dans la barre mobile. */
  primary: NavItem[];
  /** Entrées rangées sous « Plus ». Chantiers en tête, pour tous : la barre
   * mobile est déjà pleine pour qui dirige. */
  more: NavItem[];
}

export function navForRole(role: TawaRole | null): RoleNav {
  const manages = role === "manager" || role === "admin";
  return {
    primary: manages
      ? [HIER, FEUILLE, A_VALIDER, FACTURATION]
      : [HIER, FEUILLE],
    more: [
      CHANTIERS,
      TERRAIN,
      TACHES,
      PROJETS,
      ...(manages ? [EQUIPE, REGLES] : []),
      ANOMALIES,
      ...(role === "admin" ? [ADMIN] : []),
      BADGEUSE,
      PROFIL,
    ],
  };
}

/** Une journée dans la feuille de temps, dépliée. */
export function timesheetDayPath(day: string): string {
  return `/timesheet?jour=${day}`;
}

/** Création d'une règle d'imputation préremplie, depuis le temps sans projet
 * ou un chantier. Un domaine se nomme (`champ=domain`) ; dépôt et dossier se
 * déduisent du libellé ; `branche` préremplit le motif de branche. */
export function newRulePath(
  label: string,
  workerUrl?: string,
  field?: RuleMatchField,
  branch?: string,
): string {
  const q = new URLSearchParams({ libelle: label });
  if (workerUrl) q.set("salarie", workerUrl);
  if (field === "domain") q.set("champ", field);
  if (branch) q.set("branche", branch);
  return `${RULES_PATH}?${q.toString()}`;
}

/** Chemin sans sa requête, pour comparer à l'URL courante. */
export function navPathname(item: NavItem): string {
  return item.path.split("?")[0] ?? item.path;
}

/** L'entrée correspond à l'écran affiché, sous-pages comprises (/projets/:id). */
export function isNavActive(item: NavItem, pathname: string): boolean {
  const base = navPathname(item);
  return pathname === base || pathname.startsWith(`${base}/`);
}

const TITLES: NavItem[] = [
  HIER,
  { ...FEUILLE, label: "Feuille de temps" },
  A_VALIDER,
  FACTURATION,
  CHANTIERS,
  TERRAIN,
  TACHES,
  PROJETS,
  EQUIPE,
  REGLES,
  ANOMALIES,
  ADMIN,
  BADGEUSE,
  { ...PROFIL, label: "Mon profil" },
  RECAP,
  { id: "plus", label: "Plus", icon: Icons.more, path: MORE_PATH },
];

/** Nom de l'écran affiché, pour le fil d'Ariane. */
export function screenLabel(pathname: string): string {
  return TITLES.find((t) => isNavActive(t, pathname))?.label ?? "";
}
